/**
 * @file useInitTokenTest.js
 * @module src/js/hooks/useInitTokenTest
 * @description
 * **A hook to test the validity of the current authentication token.**
 * - Returns a function that:
 *   - Sends a GET request to `/auth/token-test` endpoint.
 *   - If the token is valid, returns the response data.
 *   - If the token is invalid (401), triggers logout via `fetchFromBackend`.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useInitTokenTest hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useInitTokenTest() {
    const fetchFromBackend = useFetchFromBackend();

    const initTokenTest = useCallback(async ({
        from,
        showModal = true,
        navigateToLogin = true,
    } = {}) => {
        if (navigateToLogin && from == null) {
            throw new Error("No 'from' provided when navigateToLogin is true");
        }

        const isTokenValid = await fetchFromBackend({
            request: "/auth/token-test",
            requireAuth: true,
            from,
            showModal,
            navigateToLogin,
        });

        if (isTokenValid && isTokenValid.error) {
            throw new Error("Logout should be handled in handleLogout() in fetchFromBackend()");
        }

        return isTokenValid;

    }, [fetchFromBackend]);

    return initTokenTest;
}